import React, { useEffect } from "react";
import {
  Modal,
  Form,
  Button,
  Row,
  Col,
  OverlayTrigger,
  Popover,
} from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import {
  CHEMICAL_UNITS,
  CHEMICAL_RECIPE_LOTTIE_OPTIONS,
  MODE_TYPES,
  MAX_CHEMICALS_ALLOWED,
} from "configurations/Constants";
import ChemicalInfo from "./ChemicalInfo";
import RecipeService from "services/RecipeService";
import Lottie from "react-lottie";
import BackDropLoader from "components/common/BackDropLoader";
import {
  setSavedRecipe,
  setSaveRecipeSuccess,
  setRecipeMode,
  setRecipes,
} from "reduxStore/slices/recipeSlice";
import {
  RECIPE_SAVE_ERROR,
  PLEASE_ENTER_RECIPE_NAME,
  PLEASE_SELECT_RECIPE_TYPE,
  PLEASE_ENTER_WATER_AMOUNT,
  PLEASE_FILL_REQUIRED_FIELDS_FOR_CHEMICALS,
  PLEASE_SELECT_CHEMICAL,
  PLEASE_SELECT_UNIT,
  PLEASE_ENTER_QUANTITY,
  LABEL_ENTER_RECIPE_NAME,
  LABEL_SELECT_TYPE,
  LABEL_SELECT_CHEMICAL,
  LABEL_SELECT_UNIT,
  LABEL_ENTER_WATER_AMOUNT,
  LABEL_ENTER_QUANTITY,
  WATER_QUANTITY_MUST_BE_GREATER_THAN_ZERO,
  QUANTITY_MUST_BE_GREATER_THAN_ZERO,
  ADD_CHEMICAL_RECIPE,
  EDIT_CHEMICAL_RECIPE,
  VIEW_CHEMICAL_RECIPE,
  ADD_CHEMICAL,
} from "configurations/MessageConstants";
import { toast } from "react-toastify";
import { InputHelper } from "utils/InputHelper";

const RecipeForm = ({ mode, show, onClose }) => {
  const dispatch = useDispatch();
  const recipes = useSelector((state) => state.recipe.recipes);
  const { editedRecipe, chemicals, recipeTypes } = useSelector(
    (state) => state.recipe
  );
  const defaultValues = {
    name: "",
    type: "",
    water: "",
    chemicals: [
      {
        chemicalId: null,
        name: "",
        unit: "",
        quantity: "",
      },
    ],
  };
  const isArchived = editedRecipe && editedRecipe?.archived;
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting },
    watch,
  } = useForm({ defaultValues });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "chemicals",
  });

  const watchChemicals = watch("chemicals");
  useEffect(() => {
    if (show && mode === MODE_TYPES.EDIT && editedRecipe) {
      reset({
        name: editedRecipe.name,
        type: editedRecipe.type,
        water: editedRecipe.water,
        chemicals: editedRecipe.chemicals,
      });
    }
    dispatch(setRecipeMode(mode));
  }, [show, mode, editedRecipe, dispatch, reset]);

  const handleAddChemical = () => {
    if (fields.length >= MAX_CHEMICALS_ALLOWED) {
      return;
    }
    const newId =
      fields.length > 0
        ? Math.max(...fields.map((chemical) => chemical.chemicalId || 0)) + 1
        : 1;
    append({ chemicalId: newId, name: "", unit: "", quantity: "" });
  };

  const handleRemoveChemical = (index) => {
    remove(index);
  };

  const onSubmit = async (data) => {
    try {
      const payload = {
        name: data.name,
        type: data.type,
        water: data.water,
        chemicals: data.chemicals,
      };
      const response =
        mode === MODE_TYPES.ADD
          ? await RecipeService.addRecipe(payload)
          : await RecipeService.updateRecipe(payload, editedRecipe.id);

      const updatedRecipes =
        mode === MODE_TYPES.ADD
          ? [response, ...recipes]
          : response.newRecipe && response.oldRecipe
          ? [
              response.newRecipe,
              ...recipes.map((recipe) =>
                recipe.id === response.oldRecipe.id
                  ? response.oldRecipe
                  : recipe
              ),
            ]
          : recipes.map((recipe) =>
              recipe.id === response.oldRecipe.id ? response.oldRecipe : recipe
            );
      dispatch(setRecipes(updatedRecipes));
      handleSave(response);
      handleClose();
    } catch (error) {
      console.error(RECIPE_SAVE_ERROR, error);
      toast.error(error.response.data.detail);
    }
  };

  const handleSave = (recipe) => {
    dispatch(setSavedRecipe(recipe));
    dispatch(setSaveRecipeSuccess(true));
  };

  const handleClose = () => {
    onClose();
    reset(defaultValues);
  };

  return (
    <>
      {isSubmitting && (
        <BackDropLoader
          loader={
            <Lottie
              options={CHEMICAL_RECIPE_LOTTIE_OPTIONS}
              height={200}
              width={200}
            />
          }
        />
      )}
      <Modal
        show={show}
        onHide={handleClose}
        centered
        className="recipe-form-modal"
        size="lg"
        aria-labelledby="recipe-form-popup"
      >
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Modal.Header closeButton>
            <Modal.Title id="recipe-form-popup">
              {mode === MODE_TYPES.ADD
                ? ADD_CHEMICAL_RECIPE
                : isArchived
                ? VIEW_CHEMICAL_RECIPE
                : EDIT_CHEMICAL_RECIPE}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Controller
                name="name"
                control={control}
                rules={{ required: PLEASE_ENTER_RECIPE_NAME }}
                render={({ field }) => (
                  <Form.Control
                    {...field}
                    type="text"
                    placeholder={LABEL_ENTER_RECIPE_NAME}
                    className="input-underline"
                    isInvalid={!!errors.name}
                    disabled={isArchived}
                    maxLength={50}
                  />
                )}
              />
              <Form.Control.Feedback type="invalid">
                {errors.name?.message}
              </Form.Control.Feedback>
            </Form.Group>
            <Row className="mb-3">
              <Col xs={6}>
                <Form.Group>
                  <Form.Label>Type</Form.Label>
                  <Controller
                    name="type"
                    control={control}
                    rules={{ required: PLEASE_SELECT_RECIPE_TYPE }}
                    render={({ field }) => (
                      <Form.Select
                        {...field}
                        className="input-underline"
                        aria-label="Select Type"
                        isInvalid={!!errors.type}
                        disabled={isArchived}
                      >
                        <option value="">{LABEL_SELECT_TYPE}</option>
                        {recipeTypes.map((recipeType) => (
                          <option key={recipeType.type} value={recipeType.type}>
                            {recipeType.type}
                          </option>
                        ))}
                      </Form.Select>
                    )}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.type?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col xs={6}>
                <Form.Group>
                  <Form.Label>Water Quantity (G/A)</Form.Label>
                  <Controller
                    name="water"
                    control={control}
                    rules={{
                      required: PLEASE_ENTER_WATER_AMOUNT,
                      validate: (value) =>
                        Number(value) > 0 ||
                        WATER_QUANTITY_MUST_BE_GREATER_THAN_ZERO,
                    }}
                    render={({ field }) => (
                      <Form.Control
                        {...field}
                        type="text"
                        className="input-underline"
                        isInvalid={!!errors.water}
                        disabled={isArchived}
                        placeholder={LABEL_ENTER_WATER_AMOUNT}
                        maxLength={10}
                        onChange={(e) => {
                          InputHelper.validateFloatNumberInput(
                            e,
                            field.onChange
                          );
                        }}
                      />
                    )}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.water?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0 ms-1">List of Chemicals</h6>
              {!isArchived && (
                <Button
                  type="button"
                  variant="link"
                  className="new-chemical-button"
                  onClick={handleAddChemical}
                  disabled={fields.length >= MAX_CHEMICALS_ALLOWED}
                >
                  <i className="fa fa-plus-circle me-1" />
                  <small>{ADD_CHEMICAL}</small>
                </Button>
              )}
            </div>
            {errors.chemicals && (
              <div className="validation-error-small">
                {PLEASE_FILL_REQUIRED_FIELDS_FOR_CHEMICALS}
              </div>
            )}
            {fields.length > 0 && (
              <div className="my-2">
                <Row>
                  <Col xs={4}>
                    <small className="ms-1">Chemical Name</small>
                  </Col>
                  <Col xs={4}>
                    <small className="ms-1">Unit</small>
                  </Col>
                  <Col xs={4}>
                    <small className="ms-1">Quantity (/Ac)</small>
                  </Col>
                </Row>
              </div>
            )}
            <div className="chemicals-list-container">
              {fields.map((field, index) => (
                <div key={field.chemicalId} className="mb-2">
                  <Row className="align-items-center mb-3">
                    <Col xs={4}>
                      <div className="d-flex align-items-center">
                        <Controller
                          name={`chemicals.${index}.name`}
                          control={control}
                          rules={{ required: PLEASE_SELECT_CHEMICAL }}
                          render={({ field: controllerField }) => (
                            <Form.Select
                              {...controllerField}
                              className="input-underline"
                              aria-label="Select Chemical"
                              isInvalid={!!errors.chemicals?.[index]?.name}
                              disabled={isArchived}
                              onChange={(e) => {
                                const selectedChemicalName = e.target.value;
                                const selectedChemical = chemicals.find(
                                  (chemical) =>
                                    chemical.name === selectedChemicalName
                                );
                                controllerField.onChange(selectedChemicalName);
                                setValue(
                                  `chemicals.${index}.chemicalId`,
                                  selectedChemical
                                    ? selectedChemical.chemicalId
                                    : null
                                );
                              }}
                            >
                              <option value="">{LABEL_SELECT_CHEMICAL}</option>
                              {chemicals.map((chemical) => (
                                <option
                                  key={chemical.name}
                                  value={chemical.name}
                                >
                                  {chemical.name}
                                </option>
                              ))}
                            </Form.Select>
                          )}
                        />
                        {watchChemicals?.[index]?.name && (
                          <OverlayTrigger
                            trigger={["hover", "focus"]}
                            placement="top"
                            overlay={
                              <Popover id={`chemical-info-${field.chemicalId}`}>
                                <ChemicalInfo
                                  chemicalId={watchChemicals[index].chemicalId}
                                />
                              </Popover>
                            }
                          >
                            <span>
                              <i
                                className="fa fa-info-circle ms-2"
                                title="Click for chemical information"
                              ></i>
                            </span>
                          </OverlayTrigger>
                        )}
                      </div>
                    </Col>
                    <Col xs={4}>
                      <Controller
                        name={`chemicals.${index}.unit`}
                        control={control}
                        rules={{ required: PLEASE_SELECT_UNIT }}
                        render={({ field: controllerField }) => (
                          <Form.Select
                            {...controllerField}
                            className="input-underline"
                            aria-label="Select Unit"
                            isInvalid={!!errors.chemicals?.[index]?.unit}
                            disabled={isArchived}
                          >
                            <option value="">{LABEL_SELECT_UNIT}</option>
                            {CHEMICAL_UNITS.map((unit) => (
                              <option key={unit.value} value={unit.value}>
                                {unit.name}
                              </option>
                            ))}
                          </Form.Select>
                        )}
                      />
                    </Col>
                    <Col xs={fields.length > 1 ? 3 : 4}>
                      <Controller
                        name={`chemicals.${index}.quantity`}
                        control={control}
                        rules={{
                          required: PLEASE_ENTER_QUANTITY,
                          validate: (value) =>
                            Number(value) > 0 ||
                            QUANTITY_MUST_BE_GREATER_THAN_ZERO,
                        }}
                        render={({ field: controllerField }) => (
                          <Form.Control
                            {...controllerField}
                            type="text"
                            className="input-underline"
                            isInvalid={!!errors.chemicals?.[index]?.quantity}
                            disabled={isArchived}
                            placeholder={LABEL_ENTER_QUANTITY}
                            maxLength={10}
                            onChange={(e) => {
                              InputHelper.validateFloatNumberInput(
                                e,
                                controllerField.onChange
                              );
                            }}
                          />
                        )}
                      />
                    </Col>
                    {fields.length > 1 && (
                      <Col xs={1}>
                        <Button
                          type="button"
                          variant="link"
                          className="text-muted p-0 mt-1"
                          onClick={() => handleRemoveChemical(index)}
                          disabled={isArchived}
                        >
                          <i className="fa fa-trash"></i>
                        </Button>
                      </Col>
                    )}
                  </Row>
                </div>
              ))}
            </div>
          </Modal.Body>
          {!isArchived && (
            <Modal.Footer>
              <Button variant="outline-primary" onClick={handleClose}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={isSubmitting}>
                {mode === MODE_TYPES.ADD ? "Add" : "Update"}
              </Button>
            </Modal.Footer>
          )}
        </Form>
      </Modal>
    </>
  );
};

export default RecipeForm;
